import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Paper,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  Table,
  TableCell,
  TableHead,
  TableBody,
  TableContainer,
  TableRow,
  TablePagination,
  Box,
  IconButton,
  TableSortLabel,
  Tooltip,
} from "@mui/material";

import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import DeleteRounded from "@mui/icons-material/DeleteOutlined";

import { visuallyHidden } from "@mui/utils";
import PaginationActions from "../../components/table/PaginationActions";
import API from "../../config/axios";
import { toast } from "react-hot-toast";
import debounce from "lodash.debounce";
import styles from "../../css/common.module.css";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import dayjs from "dayjs";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function TransactionHistoryList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(10);
  const [search, setSearch] = useState("");
  const abortController = useRef(new AbortController());

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTransactionId, setDeleteTransactionId] = useState(null);
  const [isDeletingTransaction, setIsDeletingTransaction] = useState(false);

  const headCells = [
    {
      id: "date",
      numeric: false,
      disablePadding: true,
      label: t("transactions.list_tbl.headers.date"),
      sortable: true,
      width: "100px",
    },

    {
      id: "amount",
      numeric: false,
      disablePadding: true,
      label: t("transactions.list_tbl.headers.bank_name"),
      sortable: false,
      width: "100px",
    },
    {
      id: "Clutch",
      numeric: false,
      disablePadding: true,
      label: t("transactions.list_tbl.headers.total_transactions"),
      sortable: false,
      width: "100px",
    },
    {
      id: "description",
      numeric: false,
      disablePadding: true,
      label: t("transactions.list_tbl.headers.opening_balance"),
      sortable: false,
      width: "200px",
    },
    {
      id: "description",
      numeric: false,
      disablePadding: true,
      label: t("transactions.list_tbl.headers.close_balance"),
      sortable: false,
      width: "200px",
    },
    {
      id: "description",
      numeric: false,
      disablePadding: true,
      label: t("transactions.list_tbl.headers.period"),
      sortable: false,
      width: "200px",
    },

    {
      id: "action",
      numeric: false,
      disablePadding: true,
      label: t("transactions.list_tbl.headers.actions"),
      sortable: false,
      width: "100px",
    },
  ];

  const showNoData = !isLoading && rows.length === 0 ? true : false;
  const skipRows = page * rowsPerPage;
  const totalColumns = headCells.length + 1;

  const createSortHandler = (property) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getInvoiceList = () => {
    // Cancel the previous request
    abortController.current.abort();
    abortController.current = new AbortController();

    setIsLoading(true);

    API.get("users/transactions/bankImport", {
      params: {
        pagination: true,
        page: page + 1,
        rowsPerPage: rowsPerPage,
        sortBy: orderBy,
        descending: order === "asc" ? false : true,
        filter: search,
      },
      signal: abortController.current.signal,
    })
      .then((res) => {
        const resData = res.data;

        if (resData.status) {
          setRows(resData.data.results);
          setTotalRows(resData.data.totalResults || 0);
          setPage(
            resData.data.page > resData.data.totalPages
              ? 0
              : resData.data.page - 1,
          );
        } else {
          toast.error("Something went wrong");
        }
      })
      .catch((error) => {
        if (!error.code || error.code !== "ERR_CANCELED") {
          toast.error("Something went wrong");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const debounceSearchInput = useMemo(() => {
    return debounce(handleSearchChange, 300);
  }, []);

  useEffect(() => {
    getInvoiceList();

    return () => {
      abortController.current.abort();
    };
    // eslint-disable-next-line
  }, [page, order, orderBy, rowsPerPage]);

  useEffect(() => {
    getInvoiceList();
    // eslint-disable-next-line
  }, [search]);

  const showDeleteConfirmationDialog = (projectId) => {
    setDeleteTransactionId(projectId);
    setShowDeleteDialog(true);
  };

  const closeDeleteConfirmationDialog = () => {
    setShowDeleteDialog(false);
    setDeleteTransactionId(null);
  };

  const deleteTransaction = () => {
    setIsDeletingTransaction(true);

    const requestStartTime = Date.now();

    API.delete(`users/transactions/bankImport/${deleteTransactionId}`)
      .then((res) => {
        const resData = res.data;

        if (resData.status) {
          toast.success(t("transactions.transactions_deleted_successfully"));
        } else {
          if (resData?.error?.message) {
            toast.error(resData?.error?.message);
          } else {
            toast.error("Something went wrong");
          }
        }
      })
      .catch(() => {
        toast.error("Something went wrong");
      })
      .finally(() => {
        closeDeleteConfirmationDialog();
        getInvoiceList();

        const requestEndTime = Date.now();
        const timeOutMileSeconds = requestEndTime - requestStartTime;

        if (timeOutMileSeconds > 300) {
          setIsDeletingTransaction(false);
        } else {
          setTimeout(() => {
            setIsDeletingTransaction(false);
          }, 300);
        }
      });
  };

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        sx={{ mb: 2 }}
        justifyContent="flex-start"
        alignItems={{ xs: "start", sm: "center" }}
        spacing={1}
      >
        <Typography component="h1" sx={{ fontSize: "20px", flexGrow: 1 }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>{" "}
          {t("transactions.import_history")}
        </Typography>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <TextField
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
            }}
            sx={{ bgcolor: "white" }}
            placeholder={t("search")}
            onChange={debounceSearchInput}
          />
        </Stack>
      </Stack>
      <Paper
        sx={{ backgroundColor: "primaryBg.light" }}
        className={styles.custom_container}
        elevation={2}
      >
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ borderRadius: "inherit" }}
        >
          <Table
            aria-label="custom pagination table"
            className={"custom-table br-0"}
            sx={{ backgroundColor: "primaryBg.light", minWidth: 600 }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: "80px",
                    minWidth: "80px",
                    textAlign: "center",
                  }}
                >
                  {t("transactions.list_tbl.headers.no")}
                </TableCell>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? "right" : "left"}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{ minWidth: headCell.width || "80px" }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : "asc"}
                        onClick={createSortHandler(headCell.id)}
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell style={{ textAlign: "center" }}>
                    {skipRows + (index + 1)}
                  </TableCell>
                  <TableCell scope="row">
                    {dayjs(row?.date).format("DD-MM-YYYY")}
                  </TableCell>
                  <TableCell scope="row">
                    {row?.paymentMethodDetails.name}
                  </TableCell>
                  <TableCell scope="row">{row?.totalTransactions}</TableCell>
                  <TableCell scope="row">
                    {row?.openingBalance.toLocaleString("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    })}
                  </TableCell>
                  <TableCell scope="row">
                    {row?.closingBalance.toLocaleString("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    })}
                  </TableCell>
                  <TableCell scope="row">
                    {dayjs(row?.transactionStartDate).format("DD-MM-YYYY")} -
                    {"  "}
                    {dayjs(row?.transactionEndDate).format("DD-MM-YYYY")}
                  </TableCell>

                  <TableCell style={{ width: "100px" }} align="left">
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Verwijderen" arrow>
                        <IconButton
                          variant="outlined"
                          color="error"
                          size="small"
                          sx={{
                            border: "2px solid currentColor",
                            width: "30px",
                            height: "30px",
                          }}
                          onClick={() => showDeleteConfirmationDialog(row.id)}
                        >
                          <DeleteRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}

              {showNoData && (
                <TableRow>
                  <TableCell colSpan={totalColumns}>
                    <Typography
                      sx={{ width: "100%", textAlign: "center", color: "grey" }}
                      variant="body2"
                    >
                      {t("noDataAvailable")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box className={styles.custom_tbl_pagination}>
          <TablePagination
            as="div"
            rowsPerPageOptions={[
              1,
              5,
              10,
              25,
              50,
              { label: "All", value: 99999999 },
            ]}
            labelRowsPerPage={t("rows_per_page")}
            colSpan={totalColumns}
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            ActionsComponent={PaginationActions}
          />
        </Box>
        <Stack direction="row" justifyContent="flex-end">
          {/* <Box mt={2} mb={2} mr={4}>
            <ExportToExcel
              headerName={{
                name: t("projects.list_tbl.headers.name"),
                "customer.name": t("projects.list_tbl.headers.customerName"),
              }}
              Data={rows}
              fileName={t("projects.title")}
            />
          </Box> */}
        </Stack>
      </Paper>
      <DeleteConfirmationDialog
        open={showDeleteDialog}
        title={t("transactions.title.delete_transactions")}
        description={t("transactions.delete_selected_transactions")}
        onClose={closeDeleteConfirmationDialog}
        onDelete={deleteTransaction}
        isDeletingData={isDeletingTransaction}
      />
    </>
  );
}
